import { StreamChat } from 'stream-chat';
import CryptoJS from 'crypto-js';
import logger from './logger';

let chatClient = null;

// Generate JWT token for GetStream using HMAC-SHA256
const generateUserToken = (userId) => {
  const apiSecret = process.env.REACT_APP_GETSTREAM_SECRET;

  if (!apiSecret) {
    logger("error", 'GetStream API secret not found');
    return null;
  }

  // Create JWT header
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };

  // Create JWT payload
  const currentTime = Math.floor(Date.now() / 1000);
  const payload = {
    user_id: userId
  };

  // Base64url encode
  const base64UrlEncode = (str) => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  // Encode header and payload
  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));

  // Create signature
  const signatureInput = `${encodedHeader}.${encodedPayload}`;
  const signature = CryptoJS.HmacSHA256(signatureInput, apiSecret);
  const encodedSignature = signature.toString(CryptoJS.enc.Base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  // Combine to create JWT
  const token = `${encodedHeader}.${encodedPayload}.${encodedSignature}`;

  return token;
};

export const initGetStreamChat = async (userId, userName, profilePhotoUrl = null) => {
  // logger("log", '🔵 Initializing GetStream chat for:', userId, userName);

  const apiKey = process.env.REACT_APP_GETSTREAM_API_KEY;
  const apiSecret = process.env.REACT_APP_GETSTREAM_SECRET;

  // logger("log", '🔵 API Key:', apiKey ? 'Found ✅' : 'Missing ❌');
  // logger("log", '🔵 API Secret:', apiSecret ? 'Found ✅' : 'Missing ❌');

  if (!apiKey) {
    logger("error", '❌ GetStream API key not found in environment variables');
    logger("error", '💡 Make sure REACT_APP_GETSTREAM_API_KEY is set in .env file');
    return null;
  }

  if (!apiSecret) {
    logger("error", '❌ GetStream API secret not found in environment variables');
    logger("error", '💡 Make sure REACT_APP_GETSTREAM_SECRET is set in .env file');
    return null;
  }

  try {
    // Create StreamChat client instance
    if (!chatClient) {
      // logger("log", '🔵 Creating new StreamChat instance...');
      chatClient = StreamChat.getInstance(apiKey);
    }

    // Check if user is already connected
    if (chatClient.userID === userId) {
      logger("log", '✅ User already connected to GetStream:', userId);
      return chatClient;
    }

    // If a different user is connected, disconnect first
    if (chatClient.userID && chatClient.userID !== userId) {
      logger("log", '🔄 Disconnecting previous user:', chatClient.userID);
      await chatClient.disconnectUser();
    }

    // Generate user token using server-side logic
    // logger("log", '🔵 Generating JWT token for user:', userId);
    const userToken = generateUserToken(userId);

    if (!userToken) {
      logger("error", '❌ Failed to generate user token');
      return null;
    }

    // logger("log", '✅ JWT token generated successfully');
    // logger("log", '🔵 Token preview:', userToken.substring(0, 50) + '...');

    // Connect user only if not already connected
    if (!chatClient.userID) {
      // logger("log", '🔵 Connecting user to GetStream...');

      // Build user object with profile photo if available
      const userObject = {
        id: userId,
        name: userName,
        role: 'user',
      };

      // Add profile photo/image URL if provided
      if (profilePhotoUrl) {
        userObject.image = profilePhotoUrl;
        logger("log", '✅ Profile photo URL added:', profilePhotoUrl);
      }

      const result = await chatClient.connectUser(
        userObject,
        userToken
      );
      logger("log", '✅ User connected successfully:', result);
    }

    return chatClient;
  } catch (error) {
    logger("error", '❌ Error initializing GetStream chat:', error);
    logger("error", '❌ Error details:', {
      message: error.message,
      code: error.code,
      status: error.status,
    });
    return null;
  }
};

export const disconnectGetStreamChat = async () => {
  try {
    if (chatClient && chatClient.userID) {
      logger("log", '🔵 Disconnecting user from GetStream:', chatClient.userID);

      // Disconnect the user properly
      await chatClient.disconnectUser();

      // Clear the client instance to force fresh connection next time
      chatClient = null;

      logger("log", '✅ GetStream chat disconnected and cleared');
    } else {
      logger("log", '🔵 No active chat client to disconnect');
    }
  } catch (error) {
    logger("error", '❌ Error disconnecting GetStream chat:', error);
    // Force clear even on error
    chatClient = null;
  }
};

export const getGetStreamClient = () => {
  return chatClient;
};

/**
 * Fetch all messages for a given Stream channel so they can be exported.
 * This uses keyset pagination under the hood to walk back through history.
 *
 * @param {string} channelType Stream channel type, e.g. "livestream" or "messaging"
 * @param {string} channelId   Stream channel id (not cid)
 * @param {number} batchSize   Page size for each query (default 100)
 * @returns {Promise<Array>}   Array of message objects ordered by created_at ascending
 */
export const fetchChannelMessagesForExport = async (
  channelType,
  channelId,
  batchSize = 100
) => {
  if (!chatClient) {
    logger("error", "GetStream chat client is not initialized");
    return [];
  }

  if (!channelType || !channelId) {
    logger("error", "Channel type and id are required to export messages");
    return [];
  }

  try {
    const channel = chatClient.channel(channelType, channelId);
    let allMessages = [];
    let lastMessageId = null;
    let safetyCounter = 0;

    // Walk backwards through history using id_lt pagination
    while (safetyCounter < 1000) {
      const messagesOptions = {
        limit: batchSize,
      };

      if (lastMessageId) {
        messagesOptions.id_lt = lastMessageId;
      }

      const response = await channel.query({
        messages: messagesOptions,
        watch: false,
      });

      const batch = Array.isArray(response?.messages)
        ? response.messages
        : [];

      if (!batch.length) break;

      allMessages = allMessages.concat(batch);

      const oldest = batch[batch.length - 1];
      lastMessageId = oldest?.id || null;

      if (batch.length < batchSize || !lastMessageId) {
        break;
      }

      safetyCounter += 1;
    }

    // De‑duplicate by id and sort ascending by created_at for a readable transcript
    const seenIds = new Set();
    const uniqueMessages = [];

    for (const msg of allMessages) {
      const id = msg?.id;
      if (!id || seenIds.has(id)) continue;
      seenIds.add(id);
      uniqueMessages.push(msg);
    }

    uniqueMessages.sort((a, b) => {
      const ta = new Date(a?.created_at || 0).getTime();
      const tb = new Date(b?.created_at || 0).getTime();
      return ta - tb;
    });

    return uniqueMessages;
  } catch (error) {
    logger("error", "Error fetching channel messages for export:", error);
    return [];
  }
};

/**
 * Convert an array of Stream messages into a plain‑text transcript.
 *
 * @param {Array} messages Stream message objects
 * @returns {string}       Human‑readable transcript
 */
export const formatMessagesAsTranscript = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }

  return messages
    .map((msg) => {
      if (!msg) return null;

      const ts = msg.created_at
        ? new Date(msg.created_at).toLocaleString()
        : "";
      const userName =
        msg.user?.name || msg.user?.id || "Unknown";
      const baseText = msg.text || "";

      const hasAttachments =
        Array.isArray(msg.attachments) && msg.attachments.length > 0;
      const attachmentsText = hasAttachments
        ? ` [attachments: ${msg.attachments
            .map(
              (a) =>
                a?.title ||
                a?.fallback ||
                a?.type ||
                "file"
            )
            .join(", ")}]`
        : "";

      const pinnedSuffix = msg.pinned ? " (pinned)" : "";

      const line = `${ts ? `[${ts}] ` : ""}${userName}: ${baseText}${attachmentsText}${pinnedSuffix}`.trim();
      return line || null;
    })
    .filter(Boolean)
    .join("\n");
};
